<script setup>
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  to: { type: [Object, String], default: '' },
  label: { type: String, default: '' },
  icon: { type: [String, Object], default: '' },
  expandable: { type: Boolean, default: false },
  isExpanded: { type: Boolean, default: false },
  isActive: { type: Boolean, default: false },
  hasActiveChild: { type: Boolean, default: false },
  getterKeys: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['toggle']);

const showBadge = useMapGetter(props.getterKeys.badge);
const dynamicCount = useMapGetter(props.getterKeys.count);
const count = computed(() =>
  dynamicCount.value > 99 ? '99+' : dynamicCount.value
);
</script>

<template>
  <component
    :is="to ? 'router-link' : 'div'"
    class="relative flex h-8 min-w-0 items-center gap-2 rounded-lg px-2 py-1 text-auth-muted transition-colors before:pointer-events-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-auth-action dark:text-auth-muted-dark dark:focus-visible:outline-auth-accent"
    role="button"
    draggable="false"
    :to="to"
    :title="label"
    :class="{
      'bg-auth-action/10 font-medium text-auth-text before:absolute before:inset-y-1 before:start-0 before:w-0.5 before:rounded-full before:bg-auth-action dark:bg-auth-accent/10 dark:text-auth-text-dark dark:before:bg-auth-accent':
        isActive || hasActiveChild,
      'hover:bg-auth-soft hover:text-auth-text dark:hover:bg-white/10 dark:hover:text-auth-text-dark':
        !isActive && !hasActiveChild,
    }"
    @click.stop="emit('toggle')"
  >
    <div v-if="icon" class="relative flex items-center gap-2">
      <Icon v-if="icon" :icon="icon" class="size-4" />
      <span
        v-if="showBadge"
        class="absolute -top-px size-2 rounded-full border border-auth-surface bg-auth-action ltr:-right-px rtl:-left-px dark:border-auth-brand-dark dark:bg-auth-accent"
      />
    </div>
    <div
      class="flex items-center gap-1.5 flex-grow justify-between min-w-0 flex-1"
    >
      <span
        class="truncate"
        :class="{
          'text-body-main': !isActive,
          'font-medium text-sm': isActive || hasActiveChild,
        }"
      >
        {{ label }}
      </span>
      <span
        v-if="dynamicCount && !expandable"
        class="inline-grid h-5 min-w-5 flex-shrink-0 place-items-center rounded-md bg-auth-soft px-1 text-xxs font-medium leading-3 text-auth-text dark:bg-white/10 dark:text-auth-text-dark"
      >
        {{ count }}
      </span>
    </div>
    <span
      v-if="expandable"
      v-show="isExpanded"
      class="i-lucide-chevron-up size-3"
      @click.stop="emit('toggle')"
    />
  </component>
</template>
